import { Location } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import pick from 'lodash-es/pick';
import { Subject } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { UpdatePatientInput } from '@app/__generated__/graphql.types';
import { UpdateAddressVariables } from '@app/account/profile/contact-information/addresses/forms/services/__generated__/UpdateAddress';
import { ParamValueMap } from '@app/shared';
import { Address, BasicAddress } from '@app/shared/address';
import { UpdatePatientGraphQLService } from '@app/shared/review-demographics/graphql/patient-demographics-graphql.service';

@Component({
  selector: 'om-edit-demographics',
  templateUrl: './edit-demographics.component.html',
})
export class EditDemographicsComponent implements OnInit, OnDestroy {
  @Output() trackPageViewed: EventEmitter<void> = new EventEmitter<void>();
  @Output() trackUpdateDataClicked: EventEmitter<UpdatePatientInput['profileInfo']> = new EventEmitter<
    UpdatePatientInput['profileInfo']
  >();

  protected isLoading: boolean;
  private destroy$ = new Subject<void>();
  isAddressEditable: boolean;
  inlineForm: UntypedFormGroup;
  defaultPreferredName: string;
  addressId: string;
  defaultPronouns: string;
  defaultGenderInfo: string;
  defaultAddressOne: string;
  defaultAddressTwo: string;
  defaultAddressCity: string;
  defaultAddressState: string;
  defaultAddressZip: string;
  formGroup: UntypedFormGroup;
  formPronounsControlName = 'pronouns';
  formPreferredControlName = 'preferred-name';
  formGenderInfoControlName = 'gender-info';
  queryParams: Params;

  constructor(
    private updatePatientGraphQL: UpdatePatientGraphQLService,
    private location: Location,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
  ) {}

  ngOnInit() {
    const {
      name,
      gender,
      pronouns,
      flow,
      addressOne,
      addressTwo,
      city,
      state,
      zip,
      addressId,
    } = this.route.snapshot.queryParams;

    this.addressId = addressId;
    this.defaultPreferredName = name;
    this.defaultGenderInfo = gender;
    this.defaultPronouns = pronouns;
    this.defaultAddressOne = addressOne;
    this.defaultAddressTwo = addressTwo;
    this.defaultAddressCity = city;
    this.defaultAddressState = state;
    this.defaultAddressZip = zip;
    this.isAddressEditable = flow === 'rx_renewal';
    if (flow === 'rx_renewal') {
      this.queryParams = { flow };
    }
    this.inlineForm = this.createInlineForm(
      this.defaultAddressState,
      this.defaultAddressZip,
      this.defaultAddressCity,
      this.defaultAddressOne,
      this.defaultAddressTwo,
    );

    const pronounsControl = new UntypedFormControl({ value: this.defaultPronouns, disabled: false });
    const preferredNameControl = new UntypedFormControl({ value: this.defaultPreferredName, disabled: false });
    const genderInfoNameControl = new UntypedFormControl({ value: this.defaultGenderInfo, disabled: false });
    this.formGroup = new UntypedFormGroup({
      [this.formPronounsControlName]: pronounsControl,
      [this.formPreferredControlName]: preferredNameControl,
      [this.formGenderInfoControlName]: genderInfoNameControl,
    });
    this.trackPageViewed.emit();
  }

  private createInlineForm(state: string, zip: string, city: string, address1: string, address2: string) {
    const value = { state, zip, city, address1, address2 };
    const controlsConfig = {
      ...Address.buildBasicAddressControlsConfig(<BasicAddress>value),
    };
    return this.formBuilder.group(controlsConfig);
  }

  validateAndSubmit(event: Event) {
    event.preventDefault();

    this.isLoading = true;

    const preferredName = this.formGroup.value[this.formPreferredControlName];
    const pronounGroup = this.formGroup.value[this.formPronounsControlName];
    const genderDetails = this.formGroup.value[this.formGenderInfoControlName];

    const profileInfo: UpdatePatientInput['profileInfo'] = {};
    let profileUpdated = false;

    if (this.isAddressEditable) {
      const updateAddressVariables: UpdateAddressVariables = this.makeUpdateAddressInput(this.inlineForm.value);
      const currentAddress = this.buildAddress(updateAddressVariables);
      const defaultAddress = this.buildAddress({
        address1: this.defaultAddressOne,
        city: this.defaultAddressCity,
        state: this.defaultAddressState,
        zip: this.defaultAddressZip,
      });

      if (currentAddress !== defaultAddress || this.defaultAddressTwo !== this.inlineForm.value['address2']) {
        profileInfo.addressesAttributes = [updateAddressVariables];
        profileUpdated = true;
      }
    }

    if (genderDetails !== this.defaultGenderInfo) {
      profileInfo.genderDetails = genderDetails;
      profileUpdated = true;
    }

    if (preferredName !== this.defaultPreferredName) {
      profileInfo.preferredName = preferredName;
      profileUpdated = true;
    }

    if (pronounGroup !== this.defaultPronouns) {
      profileInfo.pronounGroup = pronounGroup;
      profileUpdated = true;
    }

    if (!profileUpdated) {
      this.trackUpdateDataClicked.emit(profileInfo);
      this.location.back();
      this.isLoading = false;

      return;
    }

    this.updatePatientGraphQL
      .mutate({ input: { profileInfo: profileInfo } })
      .pipe(
        map(response => ({ success: response?.data?.updatePatient?.success })),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: () => {
          this.trackUpdateDataClicked.emit(profileInfo);
          this.location.back();
        },
      });
  }

  buildAddress(address: UpdateAddressVariables) {
    return `${address.address1}, ${address.city}, ${address.state}, ${address.zip}`;
  }

  private makeUpdateAddressInput(values: ParamValueMap): UpdateAddressVariables {
    const relevantProperties = ['address1', 'address2', 'city', 'isPreferred', 'kind', 'state', 'zip'];
    return { ...pick(values, relevantProperties), id: this.addressId };
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBack() {
    this.location.back();
  }
}
